import { useEffect, useMemo, useRef, useState } from 'react'
import type { Navigate } from '../App'
import type { Beat } from '../types'
import { useActiveIslands, useScenario, useSettings, useStore } from '../state/store'
import { useRecorder } from '../state/useRecorder'
import type { GradeResult } from '../lib/grade'
import { gradeFor, inputClass, languageOf, resolveMode } from '../lib/languages'
import { scoreClass } from '../lib/analysis'
import { speak, cancelSpeech } from '../lib/speech'
import { GradeReport } from '../components/GradeReport'
import { SpeakButton } from '../components/SpeakButton'
import { DrillExit } from '../components/DrillExit'

type Phase = 'brief' | 'select' | 'produce' | 'review' | 'done'

/**
 * Selection is scored separately from delivery, and weighted lighter, because
 * they fail independently: you can know a sentence cold and still freeze on
 * which one is wanted. Splitting them tells you which half is actually broken.
 */
const SELECTION_WEIGHT = 0.3
const DELIVERY_WEIGHT = 0.7

interface BeatResult {
  beatId: string
  pickedRight: boolean
  delivery: number
  total: number
}

export function ScenarioRun({
  id,
  navigate,
  setFocused,
}: {
  id: string
  navigate: Navigate
  setFocused: (v: boolean) => void
}) {
  const scenario = useScenario(id)
  const { dispatch } = useStore()
  const islands = useActiveIslands()
  const settings = useSettings()
  const record = useRecorder()

  const [phase, setPhase] = useState<Phase>('brief')
  const [index, setIndex] = useState(0)
  const [picked, setPicked] = useState<string | null>(null)
  const [attempt, setAttempt] = useState('')
  const [result, setResult] = useState<GradeResult | null>(null)
  const [results, setResults] = useState<BeatResult[]>([])
  const startedAt = useRef(Date.now())
  const lang = languageOf(scenario?.language)
  const mode = resolveMode(lang.code, settings.inputMode)

  // A beat whose island has been deleted can't be answered, so it's dropped
  // rather than left as an unanswerable dead end mid-run.
  const beats = useMemo(
    () => (scenario?.beats ?? []).filter((b) => islands.some((i) => i.id === b.expectIslandId)),
    [scenario, islands],
  )

  const beat: Beat | undefined = beats[index]
  const island = islands.find((i) => i.id === beat?.expectIslandId)

  const target = useMemo(() => {
    if (!beat || !island) return null
    const wanted = beat.expectBlockIds.length
      ? island.blocks.filter((b) => beat.expectBlockIds.includes(b.id))
      : island.blocks
    if (wanted.length === 0) return null
    return {
      hanzi: wanted.map((b) => b.hanzi).join(''),
      pinyin: wanted.map((b) => b.pinyin).join(' '),
    }
  }, [beat, island])

  useEffect(() => {
    setFocused(phase === 'select' || phase === 'produce')
  }, [phase, setFocused])

  useEffect(() => () => {
    setFocused(false)
    cancelSpeech()
  }, [setFocused])

  // The other person speaks the moment the beat opens — that's the trigger you
  // are learning to react to.
  useEffect(() => {
    if (phase !== 'select' || !beat) return
    speak(beat.promptHanzi, {
      speech: lang.speech,
      voiceURI: settings.voiceByLanguage?.[lang.code] ?? null,
      rate: settings.speechRate,
    })
  }, [phase, beat, lang.speech, settings.voiceByLanguage, settings.speechRate])

  if (!scenario) {
    return (
      <div className="empty">
        That scenario is gone.{' '}
        <button
          type="button"
          className="btn is-small"
          onClick={() => navigate({ view: 'scenarios' })}
        >
          Back to scenarios
        </button>
      </div>
    )
  }

  if (beats.length === 0) {
    return (
      <div className="empty">
        Every beat in this scenario points at an island you no longer have. Rebuild the islands, or
        restore the starter set from Settings.
      </div>
    )
  }

  const isLast = index === beats.length - 1

  const submit = () => {
    if (!beat || !target) return
    const graded = gradeFor(lang.code, { ...beat, ...target, id: beat.id, english: '' }, attempt, mode, settings.requireTones)
    setResult(graded)

    const pickedRight = picked === beat.expectIslandId
    const total = (pickedRight ? 100 : 0) * SELECTION_WEIGHT + graded.score * DELIVERY_WEIGHT
    setResults((prev) => [
      ...prev,
      { beatId: beat.id, pickedRight, delivery: graded.score, total },
    ])
    setPhase('review')
  }

  const advance = () => {
    if (isLast) {
      const finalScore =
        results.reduce((sum, r) => sum + r.total, 0) / Math.max(1, results.length)
      dispatch({
        type: 'recordScenarioRun',
        scenarioId: scenario.id,
        score: Math.round(finalScore),
      })
      // Credit lands on the island the last beat exercised, so scenario work
      // still moves the review schedule.
      if (beat) {
        record({
          islandId: beat.expectIslandId,
          mode: 'scenario',
          score: results[results.length - 1]?.delivery ?? 0,
          seconds: (Date.now() - startedAt.current) / 1000,
          inputMode: mode,
          ...(result ? { result } : {}),
        })
      }
      setPhase('done')
      return
    }

    setIndex((i) => i + 1)
    setPicked(null)
    setAttempt('')
    setResult(null)
    setPhase('select')
  }

  if (phase === 'brief') {
    return (
      <>
        <button
          type="button"
          className="btn is-quiet is-small"
          onClick={() => navigate({ view: 'scenarios' })}
          style={{ marginBottom: '0.8rem' }}
        >
          ← Back
        </button>

        <header className="page-head">
          <div className="eyebrow">{scenario.title}</div>
          <h1 className="page-title">
            <span className="page-title-zh">{scenario.titleZh}</span>
          </h1>
          <div className="hanzi muted" style={{ marginBottom: '0.6rem' }}>
            {scenario.setting}
          </div>
          <p className="lede">{scenario.brief}</p>
        </header>

        <div className="card">
          <p className="english" style={{ marginTop: 0 }}>
            {beats.length} beats. For each one you pick the island that answers it, then deliver it
            from memory. Selection is worth {SELECTION_WEIGHT * 100}% of the beat, delivery{' '}
            {DELIVERY_WEIGHT * 100}%.
          </p>
          <button
            type="button"
            className="btn is-live"
            onClick={() => {
              startedAt.current = Date.now()
              setPhase('select')
            }}
          >
            Walk in
          </button>
        </div>
      </>
    )
  }

  if (phase === 'done') {
    const finalScore = Math.round(
      results.reduce((sum, r) => sum + r.total, 0) / Math.max(1, results.length),
    )
    const misreads = results.filter((r) => !r.pickedRight).length

    return (
      <>
        <header className="page-head">
          <div className="eyebrow">{scenario.title}</div>
          <h1 className="page-title">
            <span className="page-title-zh">{lang.ui.gotOut}</span> You got out
          </h1>
        </header>

        <div className="card">
          <div className={`score ${scoreClass(finalScore)}`}>{finalScore}</div>
          <div className="stat-label">scenario score</div>

          <div className="divider" />

          <p className="english" style={{ marginTop: 0 }}>
            {misreads === 0
              ? 'You reached for the right island every time. That is the harder half.'
              : `You reached for the wrong island ${misreads} ${
                  misreads === 1 ? 'time' : 'times'
                }. Knowing the sentence is not the same as knowing when it is wanted.`}
          </p>

          <div className="tally">
            {results.map((r, i) => (
              <span key={r.beatId}>
                beat {i + 1} <span className="tally-n">{Math.round(r.total)}</span>
                {!r.pickedRight && <span style={{ color: 'var(--cinnabar)' }}> misread</span>}
              </span>
            ))}
          </div>

          <div className="divider" />

          <div className="row">
            <button
              type="button"
              className="btn is-primary"
              onClick={() => navigate({ view: 'scenarios' })}
            >
              Back to scenarios
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setPhase('brief')
                setIndex(0)
                setResults([])
                setPicked(null)
                setAttempt('')
                setResult(null)
              }}
            >
              Run it again
            </button>
          </div>
        </div>
      </>
    )
  }

  if (!beat || !island || !target) {
    /*
     * Not `return null`.
     *
     * This happens for real: delete an island, or edit away the blocks a beat
     * points at, and the run arrives here mid-scenario. It used to render
     * literally nothing — a blank page with the rail hidden and no control of
     * any kind, escapable only by reloading. Whatever else is wrong, there has
     * to be a door.
     */
    return (
      <>
        <DrillExit onLeave={() => navigate({ view: 'scenarios' })} label="Back to scenarios" />
        <div className="empty">
          This beat points at an island that is no longer here — it was probably deleted or edited
          after the scenario was written.{' '}
          <button
            type="button"
            className="btn is-small"
            onClick={() => navigate({ view: 'scenarios' })}
          >
            Back to scenarios
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {/* The live run hides the rail, so this is the only exit. Mid-scenario
          progress is not kept, which is worth saying before they press it. */}
      <DrillExit
        onLeave={() => navigate({ view: 'scenarios' })}
        label="Leave scenario"
        hint={index > 0 ? `${index} of ${beats.length} beats done — leaving does not keep them.` : undefined}
      />

      <div className="beat-dots" aria-hidden="true">
        {beats.map((b, i) => (
          <span
            key={b.id}
            className={`beat-dot ${i < index ? 'is-done' : ''} ${i === index ? 'is-current' : ''}`}
          />
        ))}
      </div>

      <div className="stage is-live">
        <span className="speaker hanzi">{beat.speaker}</span>
        <div className="hanzi" style={{ fontSize: 'clamp(1.4rem, 3.2vw, 2rem)' }}>
          {beat.promptHanzi}
        </div>
        <div className="pinyin" style={{ marginTop: '0.35rem' }}>
          {beat.promptPinyin}
        </div>

        <div className="row" style={{ marginTop: '0.7rem' }}>
          <SpeakButton text={beat.promptHanzi} language={lang.code} label="Play" withSlow />
          {phase !== 'select' && (
            <span className="muted" style={{ fontSize: '0.86rem' }}>
              {beat.promptEnglish}
            </span>
          )}
        </div>
      </div>

      {phase === 'select' && (
        <div className="card appear">
          <div className="stage-cue">{beat.cue}</div>
          <p className="english" style={{ marginTop: 0 }}>
            Which island answers this?
          </p>
          <div className="grid grid-3">
            {islands.map((option) => (
              <button
                key={option.id}
                type="button"
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  setPicked(option.id)
                  setPhase('produce')
                }}
              >
                <span className="hanzi">{option.titleZh}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {phase === 'produce' && (
        <div className="card appear">
          <div className="stage-cue">{beat.cue}</div>
          {picked !== beat.expectIslandId && (
            <div className="notice" style={{ marginBottom: '0.8rem' }}>
              You picked a different island. Answer with it anyway — you will be scored against{' '}
              <span className="hanzi">{island.titleZh}</span>, which is what the moment wanted.
            </div>
          )}
          {beat.hint && (
            <p className="muted" style={{ fontSize: '0.86rem', marginTop: 0 }}>
              {beat.hint}
            </p>
          )}

          <input
            className={`answer-input ${inputClass(mode)}`}
            value={attempt}
            autoFocus
            lang={mode === 'pinyin' ? 'en' : lang.speech}
            placeholder={mode === 'pinyin' ? 'answer…' : lang.answerPlaceholder}
            onChange={(e) => setAttempt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
              e.preventDefault()
              submit()
            }}
          />

          <div className="row" style={{ marginTop: '0.8rem' }}>
            <button
              type="button"
              className="btn is-primary"
              onClick={submit}
              disabled={!attempt.trim()}
            >
              Say it
            </button>
          </div>
        </div>
      )}

      {phase === 'review' && result && (
        <>
          <div className="card appear">
            <div className="row">
              <span className={`badge ${picked === beat.expectIslandId ? 'is-ready' : 'is-due'}`}>
                {picked === beat.expectIslandId ? 'right island' : 'misread the moment'}
              </span>
              <span className="muted" style={{ fontSize: '0.86rem' }}>
                wanted <span className="hanzi">{island.titleZh}</span>
              </span>
              <button
                type="button"
                className="btn is-primary"
                onClick={advance}
                style={{ marginLeft: 'auto' }}
              >
                {isLast ? 'Finish' : 'Next beat'}
              </button>
            </div>
          </div>
          <GradeReport result={result} mode={mode} target={target} language={lang.code} />
        </>
      )}
    </>
  )
}
